import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Tooltip,
  Divider,
  TextField,
  Button,
  InputAdornment,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  ZoomIn,
  ZoomOut,
  FitScreen,
  CropFree,
  Fullscreen,
  FullscreenExit,
  Search,
  KeyboardArrowUp,
  KeyboardArrowDown,
  Close,
  Download,
  Map as MapIcon,
  Keyboard
} from '@mui/icons-material';
import DiagramViewport from './DiagramViewport';
import DiagramMinimap from './DiagramMinimap';
import CopyCodeIcon from '../CopyCodeIcon';
import { exportDiagram } from '../../utils/diagramExport';
import logger from '../../utils/logger';

const PAN_STEP = 80;
const PAN_STEP_LARGE = 320;

const SHORTCUTS = [
  ['Ctrl / ⌘ + scroll', 'Zoom at pointer'],
  ['Scroll / drag', 'Pan'],
  ['+ / -', 'Zoom in / out'],
  ['0', 'Fit to screen'],
  ['1', 'Actual size (100%)'],
  ['Arrow keys', 'Pan (hold Shift for faster)'],
  ['Ctrl / ⌘ + F', 'Find a node'],
  ['Enter / Shift+Enter', 'Next / previous match'],
  ['F', 'Toggle fullscreen'],
  ['M', 'Toggle minimap'],
  ['Esc', 'Exit fullscreen or clear search']
];

// Widgets that consume their own key presses. MUI's Select is a
// div[role="combobox"] rather than a real <select>, so without the role check
// pressing 0/1/f/m while one is focused would drive the diagram instead of the
// dropdown's own type-ahead.
const INTERACTIVE_ROLES = '[role="combobox"], [role="listbox"], [role="menu"], [role="dialog"]';

const isTypingTarget = (target) => {
  if (!target) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable) {
    return true;
  }
  return typeof target.closest === 'function' && target.closest(INTERACTIVE_ROLES) !== null;
};

/**
 * The diagram half of the generator pages: toolbar, pan/zoom canvas, minimap
 * and export actions. Shared by Online and Offline mode.
 */
const DiagramPanel = ({
  mermaidCode,
  theme: diagramTheme,
  layout,
  busy = false,
  busyTitle,
  busySubtitle,
  title = 'Diagram Preview',
  emptyTitle,
  emptySubtitle,
  onError,
  onSuccess
}) => {
  const viewportRef = useRef(null);
  const searchInputRef = useRef(null);
  const muiTheme = useTheme();
  const isSmall = useMediaQuery(muiTheme.breakpoints.down('lg'));

  const [zoom, setZoom] = useState(100);
  const [zoomInput, setZoomInput] = useState('100');
  const [fullscreen, setFullscreen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [matchInfo, setMatchInfo] = useState({ count: 0, index: 0 });
  const [showMinimap, setShowMinimap] = useState(() => {
    try {
      return localStorage.getItem('diagramMinimap') !== 'false';
    } catch (e) {
      return true;
    }
  });
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  const [shortcutsAnchor, setShortcutsAnchor] = useState(null);
  // Bumped each time the viewport finishes a render, so the minimap knows when
  // there is a fresh SVG to clone.
  const [renderRevision, setRenderRevision] = useState(0);

  const handleRenderingChange = useCallback((isRendering) => {
    if (!isRendering) setRenderRevision((prev) => prev + 1);
  }, []);

  const handleZoomChange = useCallback((value) => {
    setZoom(value);
    setZoomInput(String(Math.round(value)));
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem('diagramMinimap', String(showMinimap));
    } catch (e) {
      // Storage unavailable (private mode) - the toggle just won't persist.
    }
  }, [showMinimap]);

  const toggleFullscreen = useCallback(() => setFullscreen((prev) => !prev), []);

  const openSearch = useCallback(() => {
    setSearchOpen(true);
    // Focus after the field has mounted.
    setTimeout(() => searchInputRef.current?.focus(), 0);
  }, []);

  const closeSearch = useCallback(() => {
    setSearchOpen(false);
    setSearchValue('');
    viewportRef.current?.search('');
  }, []);

  const runSearch = useCallback((value) => {
    setSearchValue(value);
    viewportRef.current?.search(value);
  }, []);

  const handleExport = useCallback(
    async (format) => {
      setExportMenuAnchor(null);
      const svg = viewportRef.current?.getSvgElement();
      if (!svg) {
        if (onError) onError('No diagram to export. Please generate a diagram first.');
        return;
      }
      try {
        if (onSuccess) onSuccess(`Generating ${format.toUpperCase()} export...`);
        const message = await exportDiagram(svg, format);
        if (onSuccess) onSuccess(message);
      } catch (error) {
        logger.error(`Failed to export as ${format}:`, error);
        if (onError) onError(`Failed to export diagram as ${format}: ${error.message}`);
      }
    },
    [onError, onSuccess]
  );

  const copyMermaidCode = useCallback(() => {
    navigator.clipboard
      .writeText(mermaidCode)
      .then(() => onSuccess && onSuccess('Mermaid code copied to clipboard!'))
      .catch(() => onError && onError('Failed to copy to clipboard'));
  }, [mermaidCode, onError, onSuccess]);

  // Global keyboard shortcuts.
  useEffect(() => {
    const handler = (e) => {
      const api = viewportRef.current;
      if (!api) return;

      const withModifier = e.ctrlKey || e.metaKey;

      if (withModifier && e.key.toLowerCase() === 'f' && mermaidCode) {
        e.preventDefault();
        openSearch();
        return;
      }

      if (e.key === 'Escape') {
        if (searchOpen) {
          closeSearch();
          return;
        }
        if (fullscreen) {
          setFullscreen(false);
        }
        return;
      }

      if (isTypingTarget(e.target) || withModifier || !mermaidCode) return;

      const step = e.shiftKey ? PAN_STEP_LARGE : PAN_STEP;

      switch (e.key) {
        case '+':
        case '=':
          e.preventDefault();
          api.zoomIn();
          break;
        case '-':
        case '_':
          e.preventDefault();
          api.zoomOut();
          break;
        case '0':
          e.preventDefault();
          api.fitToScreen();
          break;
        case '1':
          e.preventDefault();
          api.setZoom(100);
          break;
        case 'ArrowLeft':
          e.preventDefault();
          api.panBy(step, 0);
          break;
        case 'ArrowRight':
          e.preventDefault();
          api.panBy(-step, 0);
          break;
        case 'ArrowUp':
          e.preventDefault();
          api.panBy(0, step);
          break;
        case 'ArrowDown':
          e.preventDefault();
          api.panBy(0, -step);
          break;
        case 'f':
        case 'F':
          e.preventDefault();
          toggleFullscreen();
          break;
        case 'm':
        case 'M':
          e.preventDefault();
          setShowMinimap((prev) => !prev);
          break;
        case '/':
          e.preventDefault();
          openSearch();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [mermaidCode, fullscreen, searchOpen, openSearch, closeSearch, toggleFullscreen]);

  const commitZoomInput = () => {
    const parsed = parseInt(zoomInput, 10);
    if (Number.isNaN(parsed)) {
      setZoomInput(String(Math.round(zoom)));
      return;
    }
    viewportRef.current?.setZoom(parsed);
  };

  const hasDiagram = Boolean(mermaidCode);

  const searchField = (
    <TextField
      inputRef={searchInputRef}
      size="small"
      placeholder="Find a node..."
      value={searchValue}
      onChange={(e) => runSearch(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          if (e.shiftKey) {
            viewportRef.current?.prevMatch();
          } else {
            viewportRef.current?.nextMatch();
          }
        }
      }}
      sx={{ width: { xs: 160, sm: 220 } }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <Search fontSize="small" />
          </InputAdornment>
        ),
        endAdornment: (
          <InputAdornment position="end">
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', mr: 0.5 }}>
              {searchValue ? `${matchInfo.count ? matchInfo.index + 1 : 0}/${matchInfo.count}` : ''}
            </Typography>
            <IconButton size="small" onClick={() => viewportRef.current?.prevMatch()} disabled={!matchInfo.count}>
              <KeyboardArrowUp fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => viewportRef.current?.nextMatch()} disabled={!matchInfo.count}>
              <KeyboardArrowDown fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={closeSearch}>
              <Close fontSize="small" />
            </IconButton>
          </InputAdornment>
        )
      }}
    />
  );

  const zoomControls = (
    <Box
      sx={{
        display: 'flex',
        gap: 0.25,
        alignItems: 'center',
        border: (t) => `1px solid ${t.palette.divider}`,
        borderRadius: 1,
        px: 0.5
      }}
    >
      <Tooltip title="Zoom out (-)">
        <span>
          <IconButton onClick={() => viewportRef.current?.zoomOut()} size="small">
            <ZoomOut fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <TextField
        value={zoomInput}
        onChange={(e) => setZoomInput(e.target.value)}
        onBlur={commitZoomInput}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commitZoomInput();
            e.target.blur();
          }
        }}
        size="small"
        variant="standard"
        inputProps={{
          inputMode: 'numeric',
          style: { textAlign: 'center', width: 44, fontSize: '0.8125rem' }
        }}
        InputProps={{
          disableUnderline: true,
          endAdornment: <Typography variant="caption">%</Typography>
        }}
      />
      <Tooltip title="Zoom in (+)">
        <span>
          <IconButton onClick={() => viewportRef.current?.zoomIn()} size="small">
            <ZoomIn fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Fit to screen (0)">
        <span>
          <IconButton onClick={() => viewportRef.current?.fitToScreen()} size="small">
            <FitScreen fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Actual size (1)">
        <span>
          <IconButton onClick={() => viewportRef.current?.setZoom(100)} size="small">
            <CropFree fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );

  const exportControls = isSmall ? (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={<Download />}
        onClick={(e) => setExportMenuAnchor(e.currentTarget)}
      >
        Export
      </Button>
      <Menu
        anchorEl={exportMenuAnchor}
        open={Boolean(exportMenuAnchor)}
        onClose={() => setExportMenuAnchor(null)}
      >
        {['svg', 'png', 'pdf'].map((format) => (
          <MenuItem key={format} onClick={() => handleExport(format)}>
            <ListItemIcon>
              <Download fontSize="small" />
            </ListItemIcon>
            <ListItemText>{format.toUpperCase()}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  ) : (
    <>
      {['svg', 'png', 'pdf'].map((format) => (
        <Button
          key={format}
          variant="outlined"
          size="small"
          startIcon={<Download />}
          onClick={() => handleExport(format)}
        >
          {format.toUpperCase()}
        </Button>
      ))}
    </>
  );

  return (
    <Card
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minWidth: 0,
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
        ...(fullscreen && {
          position: 'fixed',
          inset: 0,
          zIndex: (t) => t.zIndex.modal,
          borderRadius: 0,
          height: '100vh'
        })
      }}
    >
      <CardContent
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          overflow: 'hidden',
          p: 2,
          '&:last-child': { pb: 2 }
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1.5,
            flexWrap: 'wrap',
            gap: 1
          }}
        >
          <Typography variant="h6" sx={{ mr: 1 }}>
            {title}
          </Typography>

          {hasDiagram && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              {searchOpen ? (
                searchField
              ) : (
                <Tooltip title="Find a node (Ctrl / ⌘ + F)">
                  <IconButton size="small" onClick={openSearch}>
                    <Search fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}

              {zoomControls}

              <Tooltip title={showMinimap ? 'Hide minimap (M)' : 'Show minimap (M)'}>
                <IconButton
                  size="small"
                  onClick={() => setShowMinimap((prev) => !prev)}
                  color={showMinimap ? 'primary' : 'default'}
                >
                  <MapIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title={fullscreen ? 'Exit fullscreen (F)' : 'Fullscreen (F)'}>
                <IconButton size="small" onClick={toggleFullscreen}>
                  {fullscreen ? <FullscreenExit fontSize="small" /> : <Fullscreen fontSize="small" />}
                </IconButton>
              </Tooltip>

              <Tooltip title="Keyboard shortcuts">
                <IconButton size="small" onClick={(e) => setShortcutsAnchor(e.currentTarget)}>
                  <Keyboard fontSize="small" />
                </IconButton>
              </Tooltip>

              <Divider orientation="vertical" flexItem />

              <Tooltip title="Copy Mermaid code">
                <IconButton onClick={copyMermaidCode} size="small">
                  <CopyCodeIcon />
                </IconButton>
              </Tooltip>

              {exportControls}
            </Box>
          )}
        </Box>

        <Menu
          anchorEl={shortcutsAnchor}
          open={Boolean(shortcutsAnchor)}
          onClose={() => setShortcutsAnchor(null)}
          slotProps={{ paper: { sx: { p: 1.5, maxWidth: 320 } } }}
        >
          <Typography variant="subtitle2" sx={{ px: 1, pb: 1 }}>
            Keyboard & mouse
          </Typography>
          {SHORTCUTS.map(([keys, description]) => (
            <Box
              key={keys}
              sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, px: 1, py: 0.4 }}
            >
              <Typography variant="caption" sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                {keys}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right' }}>
                {description}
              </Typography>
            </Box>
          ))}
        </Menu>

        <Divider sx={{ mb: 1.5 }} />

        <Box sx={{ flex: 1, minHeight: 0, position: 'relative', display: 'flex' }}>
          <DiagramViewport
            ref={viewportRef}
            mermaidCode={mermaidCode}
            theme={diagramTheme}
            layout={layout}
            busy={busy}
            busyTitle={busyTitle}
            busySubtitle={busySubtitle}
            emptyTitle={emptyTitle}
            emptySubtitle={emptySubtitle}
            onZoomChange={handleZoomChange}
            onRenderingChange={handleRenderingChange}
            onMatchesChange={setMatchInfo}
          />
          {showMinimap && (
            <DiagramMinimap
              viewportRef={viewportRef}
              mermaidCode={mermaidCode}
              revision={renderRevision}
            />
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default DiagramPanel;
